<template>
  <div
    class="flex flex-col w-full border rounded-xl bg-white px-2 py-0 shadow focus-within:shadow-lg transition-shadow mx-auto"
    style="max-width: 1100px;"
    @dragover.prevent="handleDragOver"
    @dragleave.prevent="handleDragLeave" 
    @drop.prevent="handleDrop"
  >
    <!-- 파일 첨부 영역 -->
    <div v-if="attachedFiles.length > 0 || uploadingFiles.length > 0" class="flex flex-row flex-wrap gap-2 items-start">
      <!-- 업로드 중/완료 파일 통합 렌더링 -->
      <div
        v-for="(file, index) in allFiles"
        :key="'file-' + (file.id || file.name) + '-' + index"
        class="inline-flex items-center bg-gray-50 rounded-lg px-4 py-2 mt-4 ml-2 min-w-[160px] max-w-[260px] shadow-sm relative"
        style="box-sizing: border-box;"
      >
        <span class="relative flex items-center justify-center mr-2" style="width:22px;height:22px;">
          <!-- 업로드 중이면 원형 로딩 인디케이터만, 완료면 파일 아이콘만 -->
          <span v-if="file.status === 'uploading'" class="flex items-center justify-center w-full h-full">
            <span class="spinner"></span>
          </span>
          <span v-else class="material-icons" style="font-size: 22px;">description</span>
        </span>
        <span
          class="text-sm cursor-pointer file-ellipsis"
          @click="openFilePreview(file)"
          style="max-width: 120px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; display: inline-block;"
        >
          {{ file.name }}
        </span>
        <span class="text-xs text-gray-500 ml-2" style="flex-shrink:0;">{{ formatSize(file.size) }}</span>
        <button
          @click="file.status === 'uploading' ? removeUploadingFile(file._uploadingIndex) : removeFile(file._attachedIndex)"
          class="text-gray-500 hover:text-gray-700 ml-4 mt-1 flex-shrink-0"
          title="파일 삭제"
        >
          <span class="material-icons text-sm">close</span>
        </button>
      </div>
    </div>

    <!-- 일반 입력창 -->
    <div class="flex-1 w-full px-0 py-0">
      <textarea
        v-model="prompt"
        :disabled="hasLoadingMessage"
        placeholder="메시지를 입력하세요..."
        rows="1"
        class="w-full resize-none border-none outline-none text-gray-700 placeholder-gray-400 text-base leading-6 mt-2"
        @input="onInput"
        @keyup.enter="handleKeyUp"
        @compositionstart="handleCompositionStart"
        @compositionend="handleCompositionEnd"
        ref="messageInput"
        :style="{
          minHeight: minHeight + 'px',
          maxHeight: '300px'
        }"
      ></textarea>
    </div>
    
    <div class="flex flex-row justify-between mt-1">
      <button
        class="w-10 h-12 flex items-center justify-center text-gray-500 hover:text-gray-700 rounded-full"
        @click="$refs.fileInput.click()"
        :disabled="hasLoadingMessage"
        title="파일 첨부"
      >
        <img src="@/assets/icons/add.png" alt="파일 첨부" class="w-10 h-10 ml-3 mb-4" :class="{ 'opacity-50': hasLoadingMessage }" />
      </button>
      <div class="flex flex-row">
        <button
          class="ml-2 w-10 h-12 flex items-center justify-center rounded-full"
          v-if="!shouldShowPauseButton"
          @click="handleSendMessage()"
          :disabled="isSendDisabled"
          title="전송"
        >
          <img src="@/assets/icons/bluebutton.png" alt="전송" class="w-10 h-10 mr-3 mb-4" :class="{ 'opacity-30 grayscale cursor-not-allowed': isSendDisabled }" />
        </button>
        <button
          class="ml-2 w-10 h-10 flex items-center justify-center rounded-full"
          v-else
          @click="$emit('pause')"
          title="일시정지"
        >
          <img src="@/assets/icons/pause.png" alt="일시정지" class="w-9 h-9 mr-6 mb-3" />
        </button>
      </div>
    </div>
    
    <input
      ref="fileInput"
      type="file"
      multiple
      class="hidden"
      @change="handleFileSelect"
      :disabled="hasLoadingMessage"
    />
    
    <FilePreviewModal
      :show="showFilePreviewModal"
      :file="previewFile"
      @close="closeFilePreview"
    />
    
    <AlertModal :visible="showAlertModal" :message="alertMessage" size="default" @confirm="showAlertModal = false" />
  </div>
</template>

<script>
import FilePreviewModal from './FilePreviewModal.vue'
import AlertModal from '@/components/common/AlertModal.vue'
import { uploadFile } from '@/utils/api'

export default {
  name: 'ChatInput',
  props: {
    placeholder: {
      type: String,
      default: '메시지 보내기'
    },
    isWaitingResponse: {
      type: Boolean,
      default: false
    },
    isTyping: {
      type: Boolean,
      default: false
    },
    minHeight: {
      type: Number,
      default: 120
    },
    messages: {
      type: Array,
      default: () => []
    }
  },
  emits: ['send-message', 'pause', 'input-resize'],
  components: {
    FilePreviewModal,
    AlertModal
  },
  data() {
    return {
      prompt: '',
      isDragging: false,
      attachedFiles: [],
      uploadingFiles: [], // 업로드 중 파일 상태 관리
      textareaHeight: 60,
      showFilePreviewModal: false,
      previewFile: null,
      showAlertModal: false,
      alertMessage: '',
      isComposing: false, // 한글 입력 조합 중 여부
    }
  },
  computed: {
    hasLoadingMessage() {
      return this.messages.some(message => message.role === 'loading') || this.isTyping;
    },
    shouldShowPauseButton() {
      return this.hasLoadingMessage || this.isTyping;
    },
    isSendDisabled() {
      return this.shouldShowPauseButton || !this.prompt.trim();
    },
    allFiles() {
      // uploadingFiles와 attachedFiles를 하나의 리스트로 합침, 인덱스 정보도 추가
      const attached = this.attachedFiles.map((f, i) => ({ ...f, status: 'done', _attachedIndex: i }))
      const uploading = this.uploadingFiles.map((f, i) => ({ ...f, status: f.status, _uploadingIndex: i }))
      return [...attached, ...uploading]
    }
  },
  methods: {
    onInput(e) {
      this.prompt = e.target.value;
      this.$nextTick(() => {
        this.autoGrow();
      });
    },
    handleKeyUp(event) {
      // Shift+Enter는 줄바꿈, Enter만 누르면 전송
      if (!event.shiftKey && !this.hasLoadingMessage) {
        event.preventDefault();
        if (this.prompt.trim() || this.attachedFiles.length > 0) {
          this.handleSendMessage();
        }
      }
    },
    handleCompositionStart() {
      this.isComposing = true;
    },
    handleCompositionEnd() {
      this.isComposing = false;
    },
    autoGrow() {
      const textarea = this.$refs.messageInput;
      if (!textarea) return;
      
      // 현재 스크롤 위치 저장
      const wasAtBottom = textarea.scrollHeight - textarea.scrollTop === textarea.clientHeight;
      
      // 높이 재설정
      textarea.style.height = this.minHeight + 'px'; // 먼저 minHeight로 초기화
      
      if (!this.prompt.trim()) {
        // 텍스트가 없으면 minHeight로 고정
        this.textareaHeight = this.minHeight;
        textarea.scrollTop = 0; // 스크롤 위치 초기화 (placeholder가 상단에 붙지 않도록)
        return;
      }
      
      const scrollHeight = textarea.scrollHeight;
      // minHeight, maxHeight 범위 내에서 높이 조절
      const newHeight = Math.min(300, Math.max(this.minHeight, scrollHeight));
      textarea.style.height = newHeight + 'px';
      this.textareaHeight = newHeight;
      
      // 스크롤 위치 복원
      if (wasAtBottom) {
        textarea.scrollTop = textarea.scrollHeight;
      }
      
      // 부모에게 높이 변화 알림
      this.$emit('input-resize', newHeight);
    },
    async handleFileSelect(event) {
      if (this.hasLoadingMessage) return
      const files = Array.from(event.target.files)
      
      // 파일 개수 제한 체크 (최대 5개)
      const currentFileCount = this.attachedFiles.length + this.uploadingFiles.length;
      const totalFileCount = currentFileCount + files.length;
      if (totalFileCount > 5) {
        this.alertMessage = `파일은 최대 5개까지만 첨부할 수 있습니다.\n현재 ${currentFileCount}개, 추가 시도 ${files.length}개 (총 ${totalFileCount}개)`;
        this.showAlertModal = true;
        event.target.value = null;
        return;
      }
      
      const MAX_SIZE = 3 * 1024 * 1024;
      for (const file of files) {
        if (file.size > MAX_SIZE) {
          this.alertMessage = '파일이 너무 큽니다 : 최대 3MB입니다';
          this.showAlertModal = true;
          event.target.value = null;
          return;
        }
      }
      await this.uploadAndAttachFiles(files)
      event.target.value = null
    },
    handleDragOver() {
      this.isDragging = true;
    },
    handleDragLeave() {
      this.isDragging = false;
    },
    async handleDrop(event) {
      if (this.hasLoadingMessage) return
      this.isDragging = false
      const files = Array.from(event.dataTransfer.files)
      if (files.length > 0) {
        // 파일 개수 제한 체크 (최대 5개)
        const currentFileCount = this.attachedFiles.length + this.uploadingFiles.length;
        const totalFileCount = currentFileCount + files.length;
        if (totalFileCount > 5) {
          this.alertMessage = `파일은 최대 5개까지만 첨부할 수 있습니다.\n현재 ${currentFileCount}개, 드롭 시도 ${files.length}개 (총 ${totalFileCount}개)`;
          this.showAlertModal = true;
          return;
        }
        
        const MAX_SIZE = 3 * 1024 * 1024;
        for (const file of files) {
          if (file.size > MAX_SIZE) {
            this.alertMessage = '파일이 너무 큽니다 : 최대 3MB입니다';
            this.showAlertModal = true;
            return;
          }
        }
        await this.uploadAndAttachFiles(files)
      }
    },
    async uploadAndAttachFiles(files) {
      const allowedTypes = ['application/pdf'] // 서버 명세상 PDF만 허용
      for (const file of files) {
        if (!allowedTypes.includes(file.type)) {
          this.alertMessage = '지원하지 않는 파일 형식입니다: ' + file.type + '\n(현재는 PDF만 업로드할 수 있습니다)';
          this.showAlertModal = true;
          continue
        }
        // 업로드 전 중복 체크 (name+size)
        const isDuplicate = this.attachedFiles.some(
          f => f.name === file.name && f.size === file.size
        );
        if (isDuplicate) {
          this.alertMessage = '이미 첨부된 파일입니다: ' + file.name;
          this.showAlertModal = true;
          continue;
        }
        // 업로드 중 파일을 임시로 추가
        const temp = {
          name: file.name,
          size: file.size,
          status: 'uploading'
        };
        this.uploadingFiles.push(temp);
        const uploadingIndex = this.uploadingFiles.length - 1;
        
        try {
          const response = await uploadFile(file);
          if (response && response.id) {
            // 업로드 성공 시 최소 0.8초간 진행 상태 보여주기
            await new Promise(resolve => setTimeout(resolve, 800));
            
            // 업로드 중 목록에서 제거
            this.uploadingFiles.splice(uploadingIndex, 1);
            
            // 완료된 파일 목록에 추가
            this.attachedFiles.push({
              id: response.id,
              name: response.name,
              size: response.size || file.size,
              originalName: file.name
            });
          } else {
            // 실패 시 업로드 중 목록에서 제거
            this.uploadingFiles.splice(uploadingIndex, 1);
            this.alertMessage = '파일 업로드에 실패했습니다.';
            this.showAlertModal = true;
          }
        } catch (error) {
          console.error('파일 업로드 오류:', error);
          // 실패 시 업로드 중 목록에서 제거
          this.uploadingFiles.splice(uploadingIndex, 1);
          this.alertMessage = '파일 업로드에 실패했습니다.';
          this.showAlertModal = true;
        }
      }
    },
    removeFile(index) {
      this.attachedFiles.splice(index, 1);
    },
    removeUploadingFile(index) {
      this.uploadingFiles.splice(index, 1);
    },
    openFilePreview(file) {
      this.previewFile = file;
      this.showFilePreviewModal = true;
    },
    closeFilePreview() {
      this.showFilePreviewModal = false;
      this.previewFile = null;
    },
    formatSize(bytes) {
      if (bytes === 0) return '0 B';
      const k = 1024;
      const sizes = ['B', 'KB', 'MB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
    },
    handleSendMessage() {
      if (this.isSendDisabled) return;
      
      const userMessage = {
        content: this.prompt.trim(),
        files: [...this.attachedFiles]
      };
      
      // 메시지 전송 이벤트 emit
      this.$emit('send-message', userMessage);
    },
    resetState() {
      this.prompt = '';
      this.attachedFiles = [];
      this.uploadingFiles = [];
      
      // textarea 높이 초기화
        this.$nextTick(() => {
          const textarea = this.$refs.messageInput;
        if (textarea) {
            textarea.style.height = this.minHeight + 'px';
            this.textareaHeight = this.minHeight;
          }
        });
      }
  }
}
</script>

<style scoped>
.spinner {
  border: 2px solid #f3f3f3;
  border-top: 2px solid #3498db;
  border-radius: 50%;
  width: 16px;
  height: 16px;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.file-ellipsis {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style> 
